import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  Tooltip,
  makeStyles,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { withStyles } from '@material-ui/styles';
import React from 'react';
import { connect } from 'react-redux';
import { Label } from '../../../../components/common/Label';
import { isEmpty } from '../../../../utils/lodash';

const useStyles = makeStyles((theme) => ({
  subRoot: {
    marginBottom: theme.spacing(4),
  },
  contactDetails: {
    padding: theme.spacing(0, 3, 4, 3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0, 1, 4, 1),
    },
  },
  inputWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    [theme.breakpoints.down('xs')]: {
      flexWrap: 'wrap',
    },
  },
  addressWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '30%',
    height: '100%',
    position: 'relative',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  inputField: {
    minWidth: theme.spacing(30),
    margin: theme.spacing(1, 0, 0, 0),
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      marginLeft: 0,
    },
    '& .Mui-focused': {
      '& label': {
        color: theme.palette.primary.main,
      },
    },
    '& .MuiInputLabel-shrink': {
      '& label': {
        fontWeight: 400,
        color: theme.palette.primary.lightBlack,
      },
    },
    '& .MuiSelect-select': { whiteSpace: 'normal' },
    '& .MuiAutocomplete-input': {
      padding: theme.spacing(0.16),
    },
  },
  dialogActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(2, 3, 2),
  },
  cancelButton: {
    borderColor: 'black',
  },
  saveButton: {
    color: 'white',
    boxShadow: 'none',
  },
}));

const styles = (theme) => ({
  root: {
    ...theme.mixins.gutters(),
    backgroundColor: theme.palette.background.paper,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    '& .MuiIconButton-root': {
      borderRadius: 0,
    },
    '& .highcharts-credits': {
      display: 'none',
    },
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    zIndex: 9999,
  },
});

export const StyledTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.tertiary.lighter,
    color: '#111111',
    borderRadius: 0,
    boxShadow: theme.shadows[1],
    fontSize: '75%',
    padding: theme.spacing(1),
    minWidth: theme.spacing(15),
    maxWidth: 350,
    maxHeight: '40vh',
    overflowY: 'auto',
    pointerEvents: 'auto',
    fontWeight: 'normal',
  },
  arrow: {
    color: 'white',
    fontSize: 14,
  },
}))(Tooltip);

const UpdateAHJRecords = (props) => {
  const {
    onClose,
    onUpdate,
    handleShowUpdateDialog,
    handleUpdateAHJ,
    statesList,
    countiesList,
    citiesList,
    updateAHJ,
    fieldsList,
    updateToList,
    disableCounty,
    disableCity,
    disableCode,
  } = props;

  const { state = '', cities = [], counties = [], field = '', update_to = [] } = updateAHJ;

  const classes = useStyles();

  const handleAllOrNoneSelection = (e, key, value) => {
    const latest = value[value.length - 1];
    const isAllOrNone = latest?.name === 'All' || latest?.name === 'None';
    if (isAllOrNone) {
      handleUpdateAHJ(e, key, [latest]);
    } else {
      handleUpdateAHJ(e, key, value);
    }
  };

  const getNoteMessage = () => {
    const isCountyNone = counties?.length === 1 && counties[0]?.name === 'None';

    const isCityAll = cities?.length === 1 && cities[0]?.name === 'All';

    const isCityNone = cities?.length === 1 && cities[0]?.name === 'None';

    if (isCityNone && isCountyNone) {
      return 'Note: Only State record(s) will get updated.';
    }
    if (isCityNone && !isCountyNone) {
      return 'Note: Only County record(s) will get updated.';
    }
    if (isCityAll && !isCountyNone) {
      return 'Note: Only City record(s) will get updated.';
    }
    return '';
  };
  const noteMessage = getNoteMessage();

  const getGoverningCodeFullName = (code) => {
    if (code && (code.name === 'Yes' || code.name === 'No')) {
      return code.name;
    } else {
      if (code) return code.short_name ? '[' + code.short_name + '] ' + code.long_name : code.long_name;
      else return '';
    }
  };

  return (
    <Box className={classes.subRoot}>
      <Dialog
        open
        onClose={handleShowUpdateDialog}
        fullWidth
        maxWidth="md"
        aria-labelledby="customized-dialog-title"
        disableEscapeKeyDown
      >
        <DialogTitle id="customized-dialog-title">
          <Box className={classes.dialogTitle}>
            <b>{'Update records'}</b>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Grid className={classes.contactDetails}>
            <Grid className={classes.inputWrapper}>
              <Box className={classes.addressWrapper}>
                <Autocomplete
                  className={classes.inputField}
                  id="state"
                  disableClearable
                  options={statesList}
                  value={state}
                  onChange={(e, value) => handleUpdateAHJ(e, 'state', value)}
                  renderInput={(params) => <TextField {...params} label={<Label text={'State'} isRequired />} />}
                />
              </Box>
              <Box className={classes.addressWrapper}>
                <Autocomplete
                  multiple
                  className={classes.inputField}
                  id="counties"
                  disableCloseOnSelect
                  disabled={isEmpty(state) || disableCounty}
                  options={countiesList || []}
                  value={counties}
                  getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                  getOptionDisabled={(option) => {
                    const selectedCounties = counties?.map((county) => county.name) || [];
                    const all = selectedCounties?.includes('All');
                    const none = selectedCounties?.includes('None');
                    if (all) return option.name !== 'All';
                    if (none) return option.name !== 'None';
                    return false;
                  }}
                  onChange={(e, value) => handleAllOrNoneSelection(e, 'counties', value)}
                  renderTags={(value, getTagProps) => {
                    if (value.length === 1 && (value[0].name === 'All' || value[0].name === 'None')) {
                      return <Typography>{value[0].name}</Typography>;
                    }
                    const numTags = value.length;
                    return (
                      <Typography>
                        {value
                          .slice(0, 1)
                          .map((option) => option.name)
                          .join(', ')}
                        {numTags > 1 && ` +${numTags - 1} more`}
                      </Typography>
                    );
                  }}
                  renderInput={(params) => <TextField {...params} label={<Label text={'County'} isRequired />} />}
                />
              </Box>
              <Box className={classes.addressWrapper}>
                <Autocomplete
                  multiple
                  className={classes.inputField}
                  id="cities"
                  disableCloseOnSelect
                  disabled={isEmpty(counties) || disableCity}
                  options={citiesList || []}
                  value={cities}
                  getOptionLabel={(option) => (typeof option === 'string' ? option : option['name'])}
                  getOptionDisabled={(option) => {
                    const selectedCities = cities?.map((city) => city.name) || [];
                    const all = selectedCities?.includes('All');
                    const none = selectedCities?.includes('None');
                    if (all) return option.name !== 'All';
                    if (none) return option.name !== 'None';
                    return false;
                  }}
                  onChange={(e, value) => handleAllOrNoneSelection(e, 'cities', value)}
                  renderTags={(value, getTagProps) => {
                    const numTags = value.length;
                    return (
                      <Typography>
                        {value
                          .slice(0, 1)
                          .map((option) => option['name'])
                          .join(', ')}
                        {numTags > 1 && ` +${numTags - 1} more`}
                      </Typography>
                    );
                  }}
                  renderInput={(params) => <TextField {...params} label={<Label text={'City'} isRequired />} />}
                />
              </Box>
            </Grid>
            <Grid className={classes.inputWrapper}>
              <Box className={classes.addressWrapper}>
                <Autocomplete
                  className={classes.inputField}
                  id="section"
                  disableClearable
                  open={false}
                  options={[]}
                  value={'Governing codes'}
                  renderInput={(params) => (
                    <TextField {...params} label={<Label text={'Section'} isRequired capitalizeLabelText={false} />} />
                  )}
                />
              </Box>
              <Box className={classes.addressWrapper}>
                <Autocomplete
                  className={classes.inputField}
                  id="field"
                  disableClearable
                  disabled={isEmpty(cities)}
                  options={fieldsList}
                  value={field}
                  getOptionLabel={(option) => (typeof option === 'string' ? option : option['name'])}
                  onChange={(e, value) => handleUpdateAHJ(e, 'field', value)}
                  title={field['name']}
                  renderInput={(params) => <TextField {...params} label={<Label text={'Field'} isRequired />} />}
                />
              </Box>
              <Box className={classes.addressWrapper}>
                {field['id'] !== 'other_codes' ? (
                  <Autocomplete
                    className={classes.inputField}
                    id="update_to"
                    disableClearable
                    disabled={isEmpty(field) || disableCode}
                    options={(updateToList || []).filter((option) => getGoverningCodeFullName(option) !== 'None')}
                    value={update_to}
                    getOptionLabel={(option) =>
                      typeof option === 'string' ? option : getGoverningCodeFullName(option)
                    }
                    title={getGoverningCodeFullName(update_to)}
                    getOptionSelected={(option, value) =>
                      getGoverningCodeFullName(option) === getGoverningCodeFullName(value)
                    }
                    onChange={(e, value) => handleUpdateAHJ(e, 'update_to', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={<Label text={'Update to'} isRequired capitalizeLabelText={false} />}
                      />
                    )}
                  />
                ) : (
                  <Autocomplete
                    multiple
                    className={classes.inputField}
                    id="update_to"
                    disableCloseOnSelect
                    disabled={isEmpty(field) || disableCode}
                    options={updateToList || []}
                    value={update_to}
                    getOptionLabel={(option) =>
                      typeof option === 'string' ? option : getGoverningCodeFullName(option)
                    }
                    getOptionSelected={(option, value) =>
                      getGoverningCodeFullName(option) === getGoverningCodeFullName(value)
                    }
                    onChange={(e, value) => handleUpdateAHJ(e, 'update_to', value)}
                    renderTags={(value, getTagProps) => {
                      const numTags = value.length;
                      return (
                        <StyledTooltip
                          title={
                            <Box>
                              {value.map((option, idx) => (
                                <Box key={idx}>{getGoverningCodeFullName(option)}</Box>
                              ))}
                            </Box>
                          }
                          arrow
                          enterTouchDelay={0}
                          leaveTouchDelay={5000}
                          interactive
                        >
                          <Typography>
                            {value
                              .slice(0, 1)
                              .map((option) => getGoverningCodeFullName(option))
                              .join(', ')}
                            {numTags > 1 && ` +${numTags - 1} more`}
                          </Typography>
                        </StyledTooltip>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={<Label text={'Update to'} isRequired capitalizeLabelText={false} />}
                      />
                    )}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
          {noteMessage && (
            <Typography variant="body2" style={{ marginTop: '1rem' }}>
              {noteMessage}
            </Typography>
          )}
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button onClick={onClose} variant="outlined" color="default" className={classes.cancelButton}>
            {'Cancel'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={classes.saveButton}
            disabled={isEmpty(update_to)}
            onClick={onUpdate}
          >
            {'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const mapDispatchToProps = (dispatch) => ({});

const mapStateToProps = (state) => ({});
export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(UpdateAHJRecords));
